<script setup lang="ts">
import { computed } from "vue";
import CustomButton from "./CustomButton.vue";

export interface CTA {
  label: string;
  handler?: (...args: unknown[]) => unknown;
  link?: { url: string, newTab?: boolean };
}

export interface Props {
  bannerType?: "info" | "success" | "warning" | "error",
  title?: string,
  text?: string,
  primaryCTA?: CTA,
  secondaryCTA?: CTA,
}

const props = withDefaults(defineProps<Props>(), {
  bannerType: "info"
});

const buttonStyle = computed(() => {
  switch (props.bannerType) {
    case "info":
    case "warning":
      return props.bannerType;
    case "error":
    case "success":
    default:
      return "primary";
  }
});
</script>

<template>
  <div :class="['banner', props.bannerType]">
    <h2 v-if="props.title" class="title">{{ props.title }}</h2>

    <div class="text">
      {{ props.text }}
      <slot name="text" />
    </div>

    <div v-if="props.primaryCTA || props.secondaryCTA" class="actions">
      <template v-if="props.primaryCTA">
        <CustomButton
          v-if="props.primaryCTA.handler"
          :label="props.primaryCTA.label"
          size="sm"
          :btnStyle="buttonStyle"
          :destructive="props.bannerType === 'error'"
          :green="props.bannerType === 'success'"
          @click="props.primaryCTA.handler"
        />
        <a
          v-else-if="props.primaryCTA.link"
          :href="props.primaryCTA.link.url"
          :target="props.primaryCTA.link.newTab ? '_blank' : ''"
        >
          <CustomButton
            :label="props.primaryCTA.label"
            size="sm"
            :btnStyle="buttonStyle"
            :destructive="props.bannerType === 'error'"
            :green="props.bannerType === 'success'"
          />
        </a>
      </template>
      <template v-if="props.secondaryCTA">
        <CustomButton
          v-if="props.secondaryCTA.handler"
          :label="props.secondaryCTA.label"
          btnStyle="secondary"
          size="sm"
          @click="props.secondaryCTA.handler"
        />
        <a
          v-else-if="props.secondaryCTA.link"
          :href="props.secondaryCTA.link.url"
          :target="props.secondaryCTA.link.newTab ? '_blank' : ''"
        >
          <CustomButton :label="props.secondaryCTA.label" btnStyle="secondary" size="sm" />
        </a>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.banner {
  padding: 15px;
  border-radius: map.get(v.$border-radius, "sm");
  border: 1px solid;
  word-break: break-word;
  &.info {
    border-color: map.get(v.$info, "200");
    background-color: map.get(v.$info, "25");
    color: map.get(v.$info, "600");
  }
  &.success {
    border-color: map.get(v.$success, "300");
    background-color: map.get(v.$success, "25");
    color: map.get(v.$success, "700");
  }
  &.warning {
    border-color: map.get(v.$warning, "300");
    background-color: map.get(v.$warning, "25");
    color: map.get(v.$warning, "700");
  }
  &.error {
    border-color: map.get(v.$error, "300");
    background-color: map.get(v.$error, "50");
    color: map.get(v.$error, "800");
  }
  .title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 10px;
  }
  .text {
    font-size: .875rem;
    line-height: 1.5;
    :deep(p:not(:last-child)) {
      margin-bottom: 10px;
    }
  }
  .actions {
    margin-top: 20px;
    display: flex;
    gap: 10px;
  }
}
</style>